import { BottomSheetModal } from '@gorhom/bottom-sheet'
import { useNavigation } from '@react-navigation/native'
import { IS_WEB } from 'constants/platform'
import { PostTypes } from 'constants/postTypes'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Pressable, StyleSheet } from 'react-native'

import { DeletePostModal } from 'partials/DeletePostModal'
import { ActionIcon } from 'partials/PostCard/subcomponents'
import { StepDescription } from 'partials/PostPlayer/widgets/StepDescription'
import { SharePostModal } from 'partials/SharePostModal'
import { WhereToBottomSheet } from 'partials/WhereToBottomSheet'

import { Avatar } from 'widgets/Avatar'
import { Box } from 'widgets/Box'
import { Modal } from 'widgets/Modal'
import { OptionItem } from 'widgets/OptionsDropdown/subcomponents/OptionItem'
import { Text } from 'widgets/Text'
import { WhereTo } from 'widgets/WhereTo/WhereTo'

import { useBookmark } from 'hooks/useBookmark'
import { useCompletion } from 'hooks/useCompletion'
import { useCompletionRoom } from 'hooks/useCompletionRoom'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useGroupsForPost } from 'hooks/useGroupsForPost'
import { useLike } from 'hooks/useLike'
import { usePostRoom } from 'hooks/usePostRoom'
import { useUser } from 'hooks/useUser'

import { links } from 'utils/constants'
import { generateDynamicLink } from 'utils/links'
import {
  copyLinkToClipboard,
  getPostLink,
  getPostLinkMessage,
  openReportForm,
} from 'utils/links/links'
import { computeMenuOptions } from 'utils/post/post'
import { handleShareAction } from 'utils/sharing'

import { useCommentModal } from 'contexts/CommentModalContext'

import { Post, StoredPostStep } from 'types/post'

const styles = StyleSheet.create({
  footer: {
    bottom: 0,
    left: 0,
    width: '100%',
    backgroundColor: 'transparent',
  },
  mt12: {
    marginTop: 12,
  },
  mt16: {
    marginTop: 16,
  },
})

export const PlayerFooter = ({
  post,
  postId,
  completionId,
  currentStep,
}: {
  post: Post | undefined
  postId: string
  completionId?: string
  currentStep: StoredPostStep | undefined
}) => {
  const commentModalRef = useCommentModal()
  const navigation = useNavigation<any>()
  const { data: currentUser } = useCurrentUser()
  const { data: postCreator } = useUser({ userId: post?.creator.id })
  const { postGroups } = useGroupsForPost(post)
  const { data: completion } = useCompletion(completionId)

  const bottomSheetRef = useRef<BottomSheetModal>(null)

  const [isSharePostModalOpen, setIsSharePostModalOpen] = useState(false)
  const [isOptionsModalVisible, setIsOptionsModalVisible] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const navigateToProfile = () => {
    navigation.navigate(links.MAIN_TABS, {
      screen: links.GROUP_STACK,
      params: {
        screen: links.MEMBER_PROFILE_PAGE,
        params: { username: post?.creator.username },
      },
    })
  }

  const isOwner = useMemo(
    () => currentUser?.id === post?.creator?.id,
    [post?.creator?.id, currentUser?.id],
  )

  const isWorkout = useMemo(() => post?.type === PostTypes.WORKOUT, [post?.type])
  const descriptionToUse: string = useMemo(
    () => (isWorkout ? currentStep?.description ?? '' : post?.description ?? ''),
    [currentStep?.description, isWorkout, post?.description],
  )

  const menuOptions = useCallback(
    () => computeMenuOptions(isOwner, isWorkout),
    [isOwner, isWorkout],
  )

  const handleEditPost = () => {
    if (isWorkout) {
      navigation.navigate(links.EDIT_WORKOUT_PAGE, {
        postId,
      })
    }
  }

  const handleDuplicatePost = () => {
    navigation.navigate(links.CREATE_POST_PAGE, {
      postId: post?.id,
    })
  }

  const [postLink, setPostLink] = useState('')
  useEffect(() => {
    generateDynamicLink(getPostLink(postId, post?.type, completionId)).then((dynamicLink) =>
      setPostLink(dynamicLink),
    )
  }, [completionId, postId, post?.type])

  const handleOnSelect = (index: number) => {
    setIsOptionsModalVisible(false)

    switch (index) {
      case 0: {
        if (isOwner) {
          if (isWorkout) {
            handleEditPost()
          } else {
            setIsDeleteModalOpen(true)
          }
        } else {
          openReportForm(currentUser, postCreator)
        }
        break
      }
      case 1:
        if (isOwner) {
          handleDuplicatePost()
        } else {
          copyLinkToClipboard(postLink)
        }
        break
      case 2:
        setIsDeleteModalOpen(true)
        break
      case 3:
        copyLinkToClipboard(postLink)
        break
      default:
        break
    }
  }

  const {
    data: isPostLiked,
    likePost,
    dislikePost,
  } = useLike({
    postId,
    userId: currentUser?.id,
  })
  const isLiked = useMemo(() => isPostLiked && post && post.likedCount > 0, [isPostLiked, post])
  const handleLikeClicked = () => {
    if (isLiked) {
      dislikePost.mutateAsync({ postId, user: currentUser })
    } else {
      likePost.mutateAsync({ postId, user: currentUser })
    }
  }

  const { saveBookmark, deleteBookmark, isBookmarked } = useBookmark({ post })
  const handleBookmarkClicked = () => {
    if (isBookmarked) {
      deleteBookmark.mutateAsync()
    } else {
      saveBookmark.mutateAsync()
    }
  }

  const openSharePostModal = async () => {
    if (!IS_WEB && post) {
      const url = getPostLink(post.id, post.type, completionId)
      await handleShareAction(url, getPostLinkMessage(post.type, true), navigation)
    } else {
      setIsSharePostModalOpen(true)
    }
  }

  const postIdForRoom = postId
  const { createRoom: createPostRoom } = usePostRoom({
    postId: postIdForRoom,
  })

  const completionIdForRoom = completionId
  const { createRoom: createCompletionRoom } = useCompletionRoom({
    completionId: completionIdForRoom,
  })

  const handleCommentClicked = async () => {
    await createCommentRoom(post?.type === PostTypes.ACTIVITY)
  }

  const createCommentRoom = useCallback(
    async (isCompletion: boolean) => {
      if ((!isCompletion && !postIdForRoom) || (isCompletion && !completionIdForRoom)) return
      const createRoom = isCompletion ? createCompletionRoom : createPostRoom
      const { id: roomId } = await createRoom.mutateAsync()

      let routeOptions: any = {
        roomId,
      }
      if (!isCompletion && postIdForRoom) {
        routeOptions = {
          ...routeOptions,
          postId: postIdForRoom,
        }
      }
      if (isCompletion && completionIdForRoom) {
        routeOptions = {
          ...routeOptions,
          completionId: completionIdForRoom,
        }
      }

      commentModalRef?.current?.present({ routeOptions })
    },
    [postIdForRoom, completionIdForRoom, createCompletionRoom, createPostRoom, commentModalRef],
  )

  const showCommentButton = useMemo(() => {
    return isOwner
  }, [isOwner])

  const commentButtonTitle = useMemo(() => {
    const postCommentCount = post?.commentsCount ? `${post?.commentsCount}` : ''
    const completionCommentCount = completion?.commentsCount ? `${completion?.commentsCount}` : ''
    return completionId ? completionCommentCount : postCommentCount
  }, [completion?.commentsCount, completionId, post?.commentsCount])

  return (
    <Box
      pointerEvents='box-none'
      style={styles.footer}
      margin={{ bottom: 88 }}
      position='absolute'
      testID='PlayerFooter'
    >
      <Box flexDirection='row' align='flex-end'>
        <Box testID='SummaryColumn' flexDirection='column' width='85%'>
          <Box
            flexDirection='row'
            align='center'
            justifyContent='flex-start'
            margin={{ left: 'smd' }}
          >
            <Pressable onPress={navigateToProfile}>
              <Text black color='white' fontSize='sm'>
                {postCreator?.username}
              </Text>
            </Pressable>
          </Box>
          {descriptionToUse && descriptionToUse.length > 0 ? (
            <StepDescription description={descriptionToUse} />
          ) : null}
          <Box
            height={28}
            flexDirection='row'
            align='center'
            justifyContent='flex-start'
            margin={{
              left: 'smd',
              top: descriptionToUse && descriptionToUse?.length > 0 ? 'xs' : 'smd',
            }}
          >
            {postCreator && post && post.combinedCategoriesAndProgramIds && (
              <WhereTo
                creator={postCreator ?? {}}
                combinedCategoriesAndProgramIds={post?.combinedCategoriesAndProgramIds ?? []}
                bottomSheetRef={bottomSheetRef}
              />
            )}
          </Box>
        </Box>
        <Box testID='ActionIconColumn' flexDirection='column' width='15%'>
          <Pressable onPress={navigateToProfile}>
            <Box margin={{ left: 15, bottom: 'lg' }}>
              <Avatar width={30} height={30} image={postCreator?.avatar} />
            </Box>
          </Pressable>
          <ActionIcon
            direction='column'
            iconSize={24}
            icon={isLiked ? 'heart-filled' : 'heart'}
            color='white'
            label={post?.likedCount !== undefined ? `${post?.likedCount}` : ''}
            action={handleLikeClicked}
          />
          {showCommentButton && (
            <>
              <Box margin='sm' />
              <ActionIcon
                direction='column'
                iconSize={24}
                icon='message-circle'
                color='white'
                label={commentButtonTitle}
                action={handleCommentClicked}
              />
            </>
          )}
          <ActionIcon
            style={styles.mt12}
            direction='column'
            iconSize={24}
            icon={isBookmarked ? 'bookmark-filled' : 'bookmark'}
            color='white'
            label=''
            action={handleBookmarkClicked}
          />
          <ActionIcon
            style={styles.mt16}
            direction='column'
            iconSize={24}
            icon='share'
            color='white'
            label=''
            action={openSharePostModal}
          />
          <ActionIcon
            style={styles.mt16}
            direction='column'
            iconSize={24}
            icon='options-menu'
            color='white'
            label=''
            action={() => setIsOptionsModalVisible(true)}
          />
        </Box>
      </Box>

      <Modal
        isVisible={isOptionsModalVisible}
        onClose={() => setIsOptionsModalVisible(false)}
        variant='fullscreenModal'
      >
        {menuOptions().map((option, index) => {
          return (
            <OptionItem
              key={option}
              option={option}
              isCancelItem={false}
              onSelect={() => {
                handleOnSelect(index)
              }}
            />
          )
        })}
        <OptionItem isCancelItem onSelect={() => setIsOptionsModalVisible(false)} />
      </Modal>

      <DeletePostModal
        handleClose={() => setIsDeleteModalOpen(false)}
        isOpen={isDeleteModalOpen}
        postId={postId}
      />

      <SharePostModal
        handleClose={() => setIsSharePostModalOpen(false)}
        isOpen={isSharePostModalOpen}
        postId={postId}
        postType={post?.type}
        completionId={completionId}
        testID={`share-post-modal-${postId}`}
      />

      <WhereToBottomSheet
        bottomSheetRef={bottomSheetRef}
        creator={postCreator}
        navigation={navigation}
        programs={postGroups}
      />
    </Box>
  )
}
